// src/quiz/gre-mock4/verbalData.js
// GRE Mock 4 — Verbal Reasoning (harder set).
// Section 1: questions 1–12 (18 min). Section 2: questions 13–27 (23 min).
// Question format: see ../greQuestionTypes.js

const SE_INSTRUCTION = "Select the two answer choices that, when used to complete the sentence, fit the meaning of the sentence as a whole and produce completed sentences that are alike in meaning.";

const ISLAND_PASSAGE =
  "The so-called island rule holds that, on islands, large-bodied mammal species tend to evolve smaller bodies while small-bodied species tend to evolve larger ones. Explanations typically invoke the distinctive ecology of islands: limited food favors smaller bodies in large animals, whereas the absence of mainland predators and competitors releases small animals from pressures that kept them small. Famous examples—dwarf elephants on Mediterranean islands, giant rats in Southeast Asia—have made the rule a staple of textbooks.\n\n" +
  "Recent analyses, however, have questioned its generality. When researchers compiled body-size data for hundreds of island populations and controlled for shared ancestry, the pattern weakened considerably: many lineages showed no consistent trend, and the striking cases appeared concentrated in a few groups. Defenders reply that the rule was never meant as an invariant law but as a description of a tendency, and that the islands in such data sets differ so greatly in size and isolation that averaging across them obscures real effects. The dispute thus turns partly on what the rule claims: a universal regularity, which the data do not support, or a conditional tendency, whose conditions have yet to be specified.";

const CONGESTION_PASSAGE =
  "Proponents of congestion pricing—charging drivers a fee to enter crowded urban zones during peak hours—argue that it reduces traffic more efficiently than building new roads, which tend to fill with induced demand. Opponents object that such fees are regressive, burdening low-income commuters most. Yet the force of this objection depends heavily on how revenues are used. In several cities, a large share of low-income commuters already travel by public transit; if fee revenues are directed toward improving that transit, the policy may on balance benefit the very groups the objection seeks to protect.";

const PHOTOGRAPHY_PASSAGE =
  "When photography emerged in the 1830s, many observers predicted that it would render realist painting obsolete. The prediction is often said to have been fulfilled indirectly: freed from the burden of documentation, painters turned toward the subjective and the abstract, and modernism followed. This account has the appeal of simplicity, but it misdescribes the relationship between the two media. For several decades, painters and photographers borrowed from one another freely. Painters used photographs as studies, sometimes adopting their cropped compositions and unconventional vantage points; photographers, seeking artistic legitimacy, staged elaborate allegorical tableaux modeled on academic painting.\n\n" +
  "Moreover, the movements most often cited as reactions against photography were in some respects deeply engaged with it. The Impressionists' interest in transient effects of light, for instance, paralleled the preoccupations of contemporary photographers experimenting with exposure times. To describe modernism as painting's retreat from a territory photography had conquered is thus to mistake a conversation for a conquest.";

const DIAGNOSIS_PASSAGE =
  "Over the past decade, the number of patients diagnosed with condition X in the region has doubled. Regional health officials conclude that condition X has become substantially more prevalent in the population.";

const CAMERA_PASSAGE =
  "After the town of Elmford installed red-light cameras at its five busiest intersections, the number of drivers running red lights at those intersections fell substantially. Yet over the same period, the total number of collisions at those intersections increased.";

const verbalQuestions = [

  // ---------- SECTION 1 ----------

  {
    id: 1,
    question: "The new curator's tenure was marked less by bold acquisitions than by ______: she spent years correcting the attributions, dates, and provenances that her predecessors had recorded carelessly.",
    options: ["punctiliousness", "profligacy", "insouciance", "venality", "grandiosity"],
    answer: "punctiliousness",
    explanation: "Years spent correcting carelessly recorded details show meticulous attention to detail: punctiliousness. Insouciance (carefree indifference) describes the predecessors, not her.",
  },
  {
    id: 2,
    type: "blanks",
    question: "Critics who found the composer's late works (i)______ had perhaps listened too casually; their apparent simplicity (ii)______ a harmonic language of considerable subtlety.",
    blanks: [
      { options: ["facile", "impenetrable", "derivative"] },
      { options: ["belies", "reflects", "announces"] },
    ],
    answer: ["facile", "belies"],
    explanation: "\"Apparent simplicity\" is what casual listeners noticed, so they found the works facile (superficially simple). The simplicity hides the real subtlety: it belies it. \"Reflects\" or \"announces\" would mean the simplicity reveals subtlety, a contradiction.",
  },
  {
    id: 3,
    type: "blanks",
    question: "The history of the bridge's construction is one of (i)______: each innovation in design seemed to (ii)______ a new problem, so that the engineers were perpetually (iii)______ difficulties of their own making.",
    blanks: [
      { options: ["unintended consequences", "steady triumph", "bureaucratic delay"] },
      { options: ["engender", "resolve", "conceal"] },
      { options: ["contending with", "anticipating", "boasting of"] },
    ],
    answer: ["unintended consequences", "engender", "contending with"],
    explanation: "\"Difficulties of their own making\" means each innovation caused (engendered) new problems, a history of unintended consequences. Engineers facing such problems are perpetually contending with them; they could hardly be anticipating problems they did not foresee.",
  },
  {
    id: 4,
    type: "blanks",
    question: "Her reputation for (i)______ notwithstanding, the judge could be surprisingly (ii)______ when a defendant showed genuine remorse.",
    blanks: [
      { options: ["severity", "leniency", "eloquence"] },
      { options: ["clement", "implacable", "verbose"] },
    ],
    answer: ["severity", "clement"],
    explanation: "\"Notwithstanding\" and \"surprisingly\" signal a contrast. Showing mercy to a remorseful defendant (clement) is surprising only for a judge known for severity.",
  },
  {
    id: 5,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The witness's account was so ______ that investigators could not establish even the basic sequence of events.",
    options: ["muddled", "lucid", "incoherent", "detailed", "consistent", "terse"],
    answer: ["muddled", "incoherent"],
    explanation: "An account that cannot yield even the basic sequence of events is confused: muddled and incoherent. Terse might explain missing detail, but has no synonym among the choices.",
  },
  {
    id: 6,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "Although he was publicly ______ about the merger, privately he had serious doubts about its wisdom.",
    options: ["sanguine", "ambivalent", "optimistic", "reticent", "apprehensive", "vocal"],
    answer: ["sanguine", "optimistic"],
    explanation: "\"Although\" contrasts his public stance with private doubts, so publicly he was confident: sanguine and optimistic. Apprehensive would match his private feelings and remove the contrast.",
  },
  {
    id: 7,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The scholar's later work is marred by a tendency toward ______: she seems unable to make any point without qualifying it into near-meaninglessness.",
    options: ["equivocation", "hedging", "assertiveness", "dogmatism", "concision", "plagiarism"],
    answer: ["equivocation", "hedging"],
    explanation: "Qualifying every point until it means almost nothing is equivocation or hedging. Dogmatism and assertiveness are the opposite tendency.",
  },
  {
    id: 8,
    passage: ISLAND_PASSAGE,
    question: "The primary purpose of the passage is to",
    options: [
      "present a debate over the scope of an evolutionary generalization",
      "argue that the island rule should be removed from textbooks",
      "explain why large mammals evolve smaller bodies on islands",
      "defend the island rule as a universal law of evolution",
      "describe a new method for measuring body size in island populations",
    ],
    answer: "present a debate over the scope of an evolutionary generalization",
    explanation: "The passage sets out the rule, the challenge to its generality, the defenders' reply, and concludes that \"the dispute thus turns partly on what the rule claims.\" The author takes no side strong enough for B or D.",
  },
  {
    id: 9,
    passage: ISLAND_PASSAGE,
    question: "The author mentions dwarf elephants and giant rats primarily in order to",
    options: [
      "illustrate the kinds of cases that helped make the island rule widely accepted",
      "show that the island rule applies equally to all mammal lineages",
      "provide evidence against the defenders of the island rule",
      "suggest that shared ancestry explains body-size changes on islands",
      "contrast Mediterranean islands with those of Southeast Asia",
    ],
    answer: "illustrate the kinds of cases that helped make the island rule widely accepted",
    explanation: "They are \"famous examples\" that \"have made the rule a staple of textbooks.\" The passage later says striking cases are concentrated in a few groups, which contradicts B.",
  },
  {
    id: 10,
    type: "multi",
    passage: ISLAND_PASSAGE,
    question: "According to the passage, defenders of the island rule argue which of the following?",
    options: [
      "The rule describes a tendency rather than an invariant law.",
      "Differences among islands can obscure real effects when data are averaged.",
      "Controlling for shared ancestry strengthens the pattern the rule describes.",
    ],
    answer: [
      "The rule describes a tendency rather than an invariant law.",
      "Differences among islands can obscure real effects when data are averaged.",
    ],
    explanation: "Defenders say the rule \"was never meant as an invariant law\" and that averaging across very different islands \"obscures real effects.\" Controlling for shared ancestry weakened the pattern, so C is false.",
  },
  {
    id: 11,
    passage: CONGESTION_PASSAGE,
    question: "The author's response to the opponents' objection is best described as",
    options: [
      "arguing that the objection's validity depends on a factor it overlooks",
      "conceding the objection and recommending that the policy be abandoned",
      "denying that low-income commuters pay congestion fees",
      "claiming that building new roads is the more equitable solution",
      "questioning the evidence that congestion pricing reduces traffic",
    ],
    answer: "arguing that the objection's validity depends on a factor it overlooks",
    explanation: "\"The force of this objection depends heavily on how revenues are used\": the author neither accepts nor rejects it outright but makes it conditional on the use of the revenue.",
  },
  {
    id: 12,
    passage: CONGESTION_PASSAGE,
    question: "Which of the following, if true, would most weaken the author's suggestion that congestion pricing may benefit low-income groups?",
    options: [
      "In the cities in question, most transit improvements funded by such fees have been directed to routes primarily serving high-income neighborhoods.",
      "Congestion pricing has reduced peak-hour traffic in several large cities.",
      "Many low-income commuters in the cities in question rely on public transit.",
      "Building new roads is often more expensive than administering congestion fees.",
      "Some high-income drivers have switched to public transit after fees were introduced.",
    ],
    answer: "In the cities in question, most transit improvements funded by such fees have been directed to routes primarily serving high-income neighborhoods.",
    explanation: "The author's suggestion requires revenues to improve the transit low-income commuters use. If the money goes to routes serving wealthier areas, the benefit to low-income groups disappears. C supports the author.",
  },

  // ---------- SECTION 2 ----------

  {
    id: 13,
    question: "Far from being ______, the novelist's revisions were driven by a coherent vision that becomes apparent only when the successive drafts are read side by side.",
    options: ["capricious", "deliberate", "extensive", "belated", "lucrative"],
    answer: "capricious",
    explanation: "\"Far from\" contrasts the blank with \"driven by a coherent vision,\" so the blank means arbitrary or whimsical: capricious.",
  },
  {
    id: 14,
    question: "The diplomat's ______ was legendary: she could deliver unwelcome news so gracefully that its recipients often thanked her for it.",
    options: ["tact", "candor", "obstinacy", "guile", "brusqueness"],
    answer: "tact",
    explanation: "Delivering bad news so gracefully that people thank you is tact. Guile implies deceit, which the sentence does not suggest; brusqueness is the opposite.",
  },
  {
    id: 15,
    type: "blanks",
    question: "The once-(i)______ field of glaciology has become a matter of urgent public interest, its findings now routinely (ii)______ in debates over coastal planning and insurance.",
    blanks: [
      { options: ["recondite", "contentious", "lucrative"] },
      { options: ["invoked", "suppressed", "ridiculed"] },
    ],
    answer: ["recondite", "invoked"],
    explanation: "\"Once\" contrasts with \"urgent public interest,\" so the field was formerly obscure and specialized: recondite. Findings of urgent interest are invoked (cited) in public debates.",
  },
  {
    id: 16,
    type: "blanks",
    question: "The minister's critics accused her of (i)______, but her defenders saw her silence differently: in their view, she declined to comment not because she had anything to (ii)______ but because she was (iii)______ about prejudging an ongoing inquiry.",
    blanks: [
      { options: ["evasiveness", "grandstanding", "impetuosity"] },
      { options: ["hide", "gain", "prove"] },
      { options: ["scrupulous", "cavalier", "sanguine"] },
    ],
    answer: ["evasiveness", "hide", "scrupulous"],
    explanation: "Critics read her silence negatively: evasiveness. Defenders deny she had anything to hide, saying instead she was careful about principle: scrupulous about prejudging an inquiry. Cavalier means careless, the opposite.",
  },
  {
    id: 17,
    type: "blanks",
    question: "The economist's forecasts, though often (i)______ in their details, have been (ii)______ in their broad outlines, which is why policymakers continue to consult them.",
    blanks: [
      { options: ["mistaken", "precise", "novel"] },
      { options: ["prescient", "misguided", "arcane"] },
    ],
    answer: ["mistaken", "prescient"],
    explanation: "\"Though\" contrasts details with broad outlines, and policymakers keep consulting the forecasts, so the outlines were accurate (prescient) while the details were often mistaken.",
  },
  {
    id: 18,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The committee's deliberations were so ______ that a decision expected within weeks took nearly two years.",
    options: ["protracted", "prolonged", "contentious", "cursory", "perfunctory", "secretive"],
    answer: ["protracted", "prolonged"],
    explanation: "Weeks stretching to two years means the deliberations were drawn out: protracted and prolonged. Contentious might explain a delay but has no partner; cursory and perfunctory are opposites.",
  },
  {
    id: 19,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "Many readers find the philosopher's prose ______, but those who persist discover that its difficulty is inseparable from the subtlety of her ideas.",
    options: ["abstruse", "recondite", "lucid", "mellifluous", "prolix", "trite"],
    answer: ["abstruse", "recondite"],
    explanation: "\"Its difficulty\" tells us the prose is hard to understand: abstruse and recondite. Prolix means wordy, which is a different problem from difficulty, and it has no partner.",
  },
  {
    id: 20,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The startup's founders, initially ______ about their prospects, grew more cautious after a series of failed product launches.",
    options: ["bullish", "sanguine", "wary", "ambivalent", "chastened", "diffident"],
    answer: ["bullish", "sanguine"],
    explanation: "They \"grew more cautious,\" so they started out confident: bullish and sanguine. Chastened describes them after the failures.",
  },
  {
    id: 21,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The reviewer ______ the film's sentimentality while acknowledging the excellence of its performances.",
    options: ["deplored", "lamented", "celebrated", "overlooked", "emulated", "exaggerated"],
    answer: ["deplored", "lamented"],
    explanation: "\"While acknowledging the excellence\" signals that the first clause is critical: the reviewer deplored or lamented the sentimentality.",
  },
  {
    id: 22,
    passage: PHOTOGRAPHY_PASSAGE,
    question: "Which of the following best expresses the main idea of the passage?",
    options: [
      "The relationship between photography and painting in the nineteenth century was more reciprocal than a common account suggests.",
      "Photography made realist painting obsolete, as many observers had predicted.",
      "Modernism arose primarily from painters' rejection of photographic techniques.",
      "Photographers gained artistic legitimacy only by abandoning allegorical subjects.",
      "The Impressionists were the first painters to use photographs as studies.",
    ],
    answer: "The relationship between photography and painting in the nineteenth century was more reciprocal than a common account suggests.",
    explanation: "The author rejects the simple story of photography displacing painting and argues the media \"borrowed from one another freely\": \"a conversation\" rather than \"a conquest.\"",
  },
  {
    id: 23,
    passage: PHOTOGRAPHY_PASSAGE,
    question: "The author mentions the allegorical tableaux staged by photographers primarily in order to",
    options: [
      "illustrate that influence between the two media ran in both directions",
      "show that photographers lacked originality",
      "explain why realist painting declined",
      "argue that academic painting was superior to photography",
      "provide an example of Impressionist technique",
    ],
    answer: "illustrate that influence between the two media ran in both directions",
    explanation: "After describing painters borrowing from photographs, the author gives photographers borrowing from painting. Together they show mutual borrowing.",
  },
  {
    id: 24,
    type: "multi",
    passage: PHOTOGRAPHY_PASSAGE,
    question: "The passage suggests which of the following about the Impressionists?",
    options: [
      "Their concerns overlapped with those of some photographers of their time.",
      "They refused to use photographs as studies for their paintings.",
      "They are often described as having reacted against photography.",
    ],
    answer: [
      "Their concerns overlapped with those of some photographers of their time.",
      "They are often described as having reacted against photography.",
    ],
    explanation: "Their interest in light \"paralleled the preoccupations of contemporary photographers\" (A), and they are offered as an instance of \"the movements most often cited as reactions against photography\" (C). Nothing says they refused to use photographs (B).",
  },
  {
    id: 25,
    passage: PHOTOGRAPHY_PASSAGE,
    question: "In the context of the passage, to \"mistake a conversation for a conquest\" most nearly means to",
    options: [
      "misinterpret mutual exchange as the displacement of one medium by another",
      "confuse critical debates about art with political events",
      "overlook the role of critics in shaping artistic movements",
      "assume that painters and photographers never communicated",
      "exaggerate the commercial success of early photography",
    ],
    answer: "misinterpret mutual exchange as the displacement of one medium by another",
    explanation: "The \"conquest\" is the account in which photography took over painting's territory; the \"conversation\" is the reciprocal borrowing the author describes.",
  },
  {
    id: 26,
    passage: DIAGNOSIS_PASSAGE,
    question: "Which of the following, if true, most seriously weakens the officials' conclusion?",
    options: [
      "During the past decade, a far more sensitive diagnostic test for condition X came into widespread use in the region.",
      "Condition X is more common among older adults than among children.",
      "The region's population grew by about 2 percent over the past decade.",
      "Treatments for condition X have become more effective during the past decade.",
      "Condition X was first described by physicians more than a century ago.",
    ],
    answer: "During the past decade, a far more sensitive diagnostic test for condition X came into widespread use in the region.",
    explanation: "More diagnoses can reflect better detection rather than more disease. A 2 percent population increase (C) is far too small to explain a doubling.",
  },
  {
    id: 27,
    passage: CAMERA_PASSAGE,
    question: "Which of the following, if true, most helps to resolve the apparent discrepancy described above?",
    options: [
      "Drivers approaching camera-monitored intersections often brake abruptly to avoid running a light, causing more rear-end collisions.",
      "Red-light cameras are expensive to install and maintain.",
      "Elmford's police department issued fewer speeding tickets during the period.",
      "Most drivers in Elmford are aware of where the cameras are located.",
      "Collisions caused by running red lights tend to be especially severe.",
    ],
    answer: "Drivers approaching camera-monitored intersections often brake abruptly to avoid running a light, causing more rear-end collisions.",
    explanation: "This explains how fewer red-light violations can coexist with more collisions: the cameras change behavior in a way that produces a different type of crash.",
  },
];

export default verbalQuestions;
